import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  Button,
  Table,
  Form,
  Badge,
  Spinner,
  Alert,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { FaPlus, FaTrash, FaUndo } from 'react-icons/fa';
import jwt_decode from 'jwt-decode';
import LookupLayout from './LookupLayout';
import MultiSelectDropdown from './MultiSelectDropdown1';
import ObjectConfigApi from '../../api/ObjectConfigApi';

const normalizeBoolean = value => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    return lower === 'true' || lower === '1' || lower === 'yes';
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  return false;
};

const formatDateValue = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
};

const formatDateTimeValue = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const iso = date.toISOString();
  return iso.slice(0, 16);
};

const formatTimeValue = value => {
  if (!value) return '';
  // If it's a full datetime string, extract just the time part
  if (value.includes('T') || value.includes(' ')) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');
  }
  // If it's already in HH:MM:SS format, extract just HH:MM
  if (value.includes(':')) {
    const parts = value.split(':');
    return parts[0] + ':' + parts[1];
  }
  return value;
};

const generateRowKey = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const REQUIRED_INDICATOR_COLOR = '#ff5d5d';
const REQUIRED_ASTERISK_STYLE = {
  color: REQUIRED_INDICATOR_COLOR,
  marginRight: 4,
  fontWeight: 600,
  lineHeight: 1,
};
const REQUIRED_INPUT_STYLE = {
  boxShadow: `inset 3px 0 0 0 ${REQUIRED_INDICATOR_COLOR}`,
  borderColor: '#ced4da',
};

const BulkEditLayout = ({
  show,
  onHide,
  tab,
  selectedRecords = [],
  parentRecordId,
  parentObjectName,
}) => {
  const [rows, setRows] = useState([]);
  const [initialising, setInitialising] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState('');
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [currentOwner, setCurrentOwner] = useState(null);

  const bulkFieldNames = useMemo(() => {
    const bulkFields =
      tab?.config?.field_config?.bulk_action_fields ||
      tab?.config?.field_config?.fields ||
      [];
    return Array.isArray(bulkFields) ? bulkFields : [];
  }, [tab]);

  const objectFieldsMap = useMemo(() => {
    const map = {};
    if (Array.isArray(fieldConfigs)) {
      fieldConfigs.forEach(field => {
        if (field?.field_name) {
          map[field.field_name] = field;
        }
      });
    }
    if (Array.isArray(tab?.object_fields)) {
      tab.object_fields.forEach(field => {
        if (field?.field_name && !map[field.field_name]) {
          map[field.field_name] = field;
        }
      });
    }
    return map;
  }, [fieldConfigs, tab]);

  const activeFieldNames = useMemo(
    () => bulkFieldNames.filter(name => objectFieldsMap[name]),
    [bulkFieldNames, objectFieldsMap]
  );

  const ownerFieldName = useMemo(() => {
    return activeFieldNames.find(name => {
      const meta = objectFieldsMap[name] || {};
      const referenceObject =
        (meta.reference_object || meta.referenceObject || '').toLowerCase();
      if (referenceObject !== 'user') {
        return false;
      }
      const lowerName = (name || '').toLowerCase();
      const lowerLabel = (meta.field_label || meta.label || '').toLowerCase();
      return lowerName.includes('ownerid') || lowerLabel.includes('owner');
    });
  }, [activeFieldNames, objectFieldsMap]);

  const baseRequiredSet = useMemo(() => {
    const set = new Set();
    activeFieldNames.forEach(fieldName => {
      const meta = objectFieldsMap[fieldName] || {};
      if (
        meta.is_required === true ||
        meta.isRequired === true ||
        meta.required === true
      ) {
        set.add(fieldName);
      }
    });
    return set;
  }, [bulkFieldNames, objectFieldsMap]);

  const layoutRequiredSet = useMemo(() => {
    const names = tab?.config?.field_config?.required_fields || [];
    return new Set(names);
  }, [tab]);

  const bulkLayoutRequiredSet = useMemo(() => {
    const names = tab?.config?.field_config?.bulk_action_required_fields || [];
    return new Set(names);
  }, [tab]);

  const isFieldRequired = fieldName =>
    baseRequiredSet.has(fieldName) ||
    layoutRequiredSet.has(fieldName) ||
    bulkLayoutRequiredSet.has(fieldName);

  const getCurrentUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const decoded = jwt_decode(token);
        console.debug('[BulkEditLayout] Decoded current user token payload', decoded);
        return {
          id: decoded.id,
          firstname: decoded.firstname,
          lastname: decoded.lastname,
          username: decoded.username,
          email: decoded.email,
        };
      }
    } catch (error) {
      console.error('Failed to decode current user token:', error);
    }
    return null;
  }, []);

  const createRowFromRecord = record => {
    const values = {};
    const displayValues = {};
    (record?.fields || []).forEach(field => {
      if (!field?.fieldName) return;
      values[field.fieldName] =
        field.value !== undefined && field.value !== null ? field.value : '';
      if (field.displayValue !== undefined && field.displayValue !== null) {
        displayValues[field.fieldName] = field.displayValue;
      }
    });

    const recordId =
      record?.id ||
      (record?.fields || []).find(f => f.fieldName?.toLowerCase() === 'id')
        ?.value ||
      generateRowKey();

    return {
      key: recordId,
      mode: 'existing',
      recordId,
      values,
      originalValues: { ...values },
      displayValues,
    };
  };

  const createEmptyRow = () => {
    const values = {};
    activeFieldNames.forEach(name => {
      values[name] = '';
    });
    if (ownerFieldName && currentOwner?.id) {
      values[ownerFieldName] = currentOwner.id;
    }
    return {
      key: generateRowKey(),
      mode: 'new',
      values,
      originalValues: { ...values },
      displayValues: ownerFieldName && currentOwner?.label ? { [ownerFieldName]: currentOwner.label } : {},
    };
  };

  useEffect(() => {
    if (!show) return;

    setInitialising(true);

    const preloadedRows = selectedRecords.map(record =>
      createRowFromRecord(record)
    );
    const initialRows =
      preloadedRows.length > 0 ? preloadedRows : [createEmptyRow()];
    setRows(initialRows);

    const timeoutId = window.setTimeout(() => setInitialising(false), 0);
    return () => window.clearTimeout(timeoutId);
  }, [show, selectedRecords, activeFieldNames]);

  useEffect(() => {
    if (!show) return;
    const targetObjectName = tab?.config?.target_object_name;
    if (!targetObjectName) {
      setFieldConfigs([]);
      return;
    }

    let isMounted = true;
    setConfigLoading(true);
    setConfigError('');

    ObjectConfigApi.fetchConfigObjectByNameWithFields(targetObjectName)
      .then(response => {
        if (!isMounted) return;
        const result = response?.data;
        if (result?.fields) {
          setFieldConfigs(result.fields);
        } else {
          setFieldConfigs([]);
          setConfigError('No field configuration found for this object.');
        }
      })
      .catch(error => {
        if (!isMounted) return;
        console.error('Failed to load object configuration:', error);
        setFieldConfigs([]);
        setConfigError(
          error?.message || 'Failed to load field configuration.'
        );
      })
      .finally(() => {
        if (isMounted) setConfigLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [show, tab]);

  const handleAddRow = () => {
    setRows(prev => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = rowKey => {
    setRows(prev => prev.filter(row => row.key !== rowKey));
  };

  const handleResetRow = rowKey => {
    setRows(prev =>
      prev.map(row => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          values: { ...row.originalValues },
        };
      })
    );
  };

  const handleFieldChange = (rowKey, fieldName, newValue) => {
    setRows(prev =>
      prev.map(row => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          values: {
            ...row.values,
            [fieldName]: newValue,
          },
        };
      })
    );
  };

  const handleLookupChange = (rowKey, fieldName, newId, displayValue) => {
    setRows(prev =>
      prev.map(row => {
        if (row.key !== rowKey) return row;
        return {
          ...row,
          values: {
            ...row.values,
            [fieldName]: newId,
          },
          displayValues: {
            ...row.displayValues,
            [fieldName]: displayValue,
          },
        };
      })
    );
  };

  useEffect(() => {
    if (!show || !ownerFieldName) return;

    const userInfo = getCurrentUserFromToken();
    if (!userInfo?.id) return;

    const ownerLabel =
      [userInfo.firstname, userInfo.lastname].filter(Boolean).join(' ').trim() ||
      userInfo.fullname ||
      userInfo.username ||
      userInfo.email ||
      'Current User';

    console.debug('[BulkEditLayout] Using current user from token for owner', {
      id: userInfo.id,
      label: ownerLabel,
    });

    setCurrentOwner({ id: userInfo.id, label: ownerLabel });
  }, [show, ownerFieldName, getCurrentUserFromToken]);

  useEffect(() => {
    if (!show || !ownerFieldName || !currentOwner?.id) return;

    setRows(prev => {
      let changed = false;
      const updatedRows = prev.map(row => {
        if (row.mode !== 'new') {
          return row;
        }

        const currentValue = row.values?.[ownerFieldName];
        const currentDisplay = row.displayValues?.[ownerFieldName];
        const needsValue = !currentValue;
        const needsDisplayUpdate =
          currentValue === currentOwner.id && currentDisplay !== currentOwner.label;

        if (!needsValue && !needsDisplayUpdate) {
          return row;
        }

        const nextValues = {
          ...row.values,
          [ownerFieldName]: currentOwner.id,
        };

        const nextDisplayValues = {
          ...row.displayValues,
          [ownerFieldName]: currentOwner.label,
        };

        const nextOriginalValues = needsValue
          ? {
            ...row.originalValues,
            [ownerFieldName]: currentOwner.id,
          }
          : row.originalValues;

        const nextRow = {
          ...row,
          values: nextValues,
          originalValues: nextOriginalValues,
          displayValues: nextDisplayValues,
        };
        if (!changed) {
          changed =
            nextRow.values !== row.values ||
            nextRow.displayValues !== row.displayValues ||
            nextRow.originalValues !== row.originalValues;
        }
        return nextRow;
      });
      if (changed) {
        console.debug('[BulkEditLayout] Applied owner defaults to rows', {
          ownerFieldName,
          currentOwner,
          affectedRows: updatedRows.filter(
            row => row.mode === 'new' && row.values?.[ownerFieldName] === currentOwner.id
          ).length,
        });
      }
      return updatedRows;
    });
  }, [show, ownerFieldName, currentOwner]);

  const renderInputForField = (row, fieldName) => {
    const meta =
      objectFieldsMap[fieldName] ||
      fieldConfigs.find(field => field.field_name === fieldName) ||
      {};
    const dataType = (meta.data_type || meta.type || '').toLowerCase();
    const value =
      row.values[fieldName] !== undefined ? row.values[fieldName] : '';
    const isRequired = isFieldRequired(fieldName);
    const requiredStyle = isRequired ? REQUIRED_INPUT_STYLE : undefined;

    const commonProps = {
      value: value ?? '',
      onChange: event => handleFieldChange(row.key, fieldName, event.target.value),
      style: requiredStyle,
    };

    if (dataType === 'textarea' || dataType === 'richtext') {
      return (
        <Form.Control
          as='textarea'
          rows={2}
          {...commonProps}
        />
      );
    }

    if (dataType === 'date') {
      return (
        <Form.Control
          type='date'
          value={formatDateValue(value)}
          onChange={event =>
            handleFieldChange(row.key, fieldName, event.target.value)
          }
          style={requiredStyle}
        />
      );
    }

    if (dataType === 'datetime' || dataType === 'datetime2') {
      return (
        <Form.Control
          type='datetime-local'
          value={formatDateTimeValue(value)}
          onChange={event =>
            handleFieldChange(row.key, fieldName, event.target.value)
          }
          style={requiredStyle}
        />
      );
    }

    if (dataType === 'time') {
      return (
        <Form.Control
          type='time'
          value={formatTimeValue(value)}
          onChange={event =>
            handleFieldChange(row.key, fieldName, event.target.value)
          }
          style={requiredStyle}
        />
      );
    }

    if (dataType === 'boolean' || dataType === 'checkbox') {
      return (
        <div className='d-flex justify-content-center'>
          <Form.Check
            type='checkbox'
            checked={normalizeBoolean(value)}
            onChange={event =>
              handleFieldChange(row.key, fieldName, event.target.checked)
            }
          />
        </div>
      );
    }

    if (dataType === 'picklist') {
      const options = meta.picklist_values || meta.picklistValues || [];
      return (
        <Form.Select
          value={value ?? ''}
          onChange={event =>
            handleFieldChange(row.key, fieldName, event.target.value)
          }
          style={requiredStyle}
        >
          <option value=''>-- Select --</option>
          {options.map(option => (
            <option
              key={option.key || option.value_key || option.value}
              value={option.key || option.value_key || option.value}
            >
              {option.value || option.value_label || option.label || option.key}
            </option>
          ))}
        </Form.Select>
      );
    }

    if (dataType === 'multipicklist') {
      const rawOptions = meta.picklist_values || meta.picklistValues || [];
      const picklistValues = rawOptions.map(option => ({
        key: option.key || option.value_key || option.value,
        value: option.value || option.value_label || option.label || option.key,
      }));

      const selectedValues =
        typeof value === 'string' ? value.split(';').filter(Boolean) : Array.isArray(value) ? value : [];

      const handleMultiSelectChange = selectedArray => {
        handleFieldChange(row.key, fieldName, selectedArray.join(';'));
      };

      const isDisabled =
        meta.is_readonly === true ||
        meta.isReadonly === true ||
        meta.readonly === true;

      return (
        <MultiSelectDropdown
          selectedValues={selectedValues}
          picklistValues={picklistValues}
          onChange={handleMultiSelectChange}
          isRequired={isRequired}
          placeholder='Select...'
          disabled={isDisabled}
        />
      );
    }

    if (
      dataType === 'number' ||
      dataType === 'currency' ||
      dataType === 'percent' ||
      dataType === 'double' ||
      dataType === 'int'
    ) {
      return (
        <Form.Control
          type='number'
          value={value ?? ''}
          onChange={event =>
            handleFieldChange(row.key, fieldName, event.target.value)
          }
          style={requiredStyle}
        />
      );
    }

    if (dataType === 'lookup') {
      return (
        <LookupLayout
          objectName={meta.reference_object}
          searchFields={meta.lookup_search_fields || 'name'}
          displayFields={meta.display_fields || 'name'}
          value={value}
          defaultLabel={row.displayValues?.[fieldName] || ''}
          onChange={(id, label) =>
            handleLookupChange(row.key, fieldName, id, label)
          }
          isRequired={isRequired}
        />
      );
    }

    return (
      <Form.Control
        type='text'
        {...commonProps}
      />
    );
  };

  const renderHeaderCell = (fieldName, meta) => {
    const label = meta?.field_label || meta?.label || fieldName;
    const required = isFieldRequired(fieldName);
    return (
      <div className='d-flex align-items-center gap-2'>
        <span className='d-flex align-items-center'>
          {required && (
            <span style={REQUIRED_ASTERISK_STYLE} title='Required' aria-hidden='true'>
              *
            </span>
          )}
          <span>{label}</span>
        </span>
        {rowContainsDisplayValue(fieldName) && (
          <OverlayTrigger
            placement='top'
            overlay={<Tooltip id={`tooltip-${fieldName}`}>Original display value (read-only)</Tooltip>}
          >
            <Badge bg='secondary'>Lookup</Badge>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const rowContainsDisplayValue = fieldName =>
    rows.some(row => row.displayValues && row.displayValues[fieldName]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      size='xl'
      centered
      backdrop='static'
      className='bulk-edit-layout-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {tab?.tabName ? `${tab.tabName} - Add / Edit` : 'Bulk Editor'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {initialising || configLoading ? (
          <div className='d-flex align-items-center justify-content-center py-5'>
            <Spinner animation='border' role='status' className='me-3' />
            <span>Loading related records...</span>
          </div>
        ) : configError ? (
          <Alert variant='danger'>{configError}</Alert>
        ) : activeFieldNames.length === 0 ? (
          <Alert variant='info'>
            No bulk action fields have been configured for this related list. Please update the layout configuration to enable bulk editing.
          </Alert>
        ) : (
          <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
              <div>
                <strong>Parent Record:</strong>{' '}
                <code>{parentObjectName}/{parentRecordId}</code>
              </div>
              <Button variant='outline-primary' size='sm' onClick={handleAddRow}>
                <FaPlus className='me-2' />
                Add Row
              </Button>
            </div>

            <Table bordered responsive hover size='sm'>
              <thead className='table-light'>
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  {activeFieldNames.map(fieldName => {
                    const meta = objectFieldsMap[fieldName] || {};
                    return (
                      <th key={fieldName}>
                        {renderHeaderCell(fieldName, meta)}
                      </th>
                    );
                  })}
                  <th style={{ width: '140px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.key}>
                    <td className='text-center align-middle'>
                      {row.mode === 'existing' ? (
                        <Badge bg='secondary'>Existing</Badge>
                      ) : (
                        <Badge bg='success'>New</Badge>
                      )}
                      <div className='small text-muted'>{index + 1}</div>
                    </td>
                    {activeFieldNames.map(fieldName => (
                      <td key={`${row.key}-${fieldName}`}>
                        {renderInputForField(row, fieldName)}
                      </td>
                    ))}
                    <td className='text-center align-middle'>
                      <div className='d-flex justify-content-center gap-2'>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip id={`reset-${row.key}`}>Reset Row</Tooltip>}
                        >
                          <span>
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => handleResetRow(row.key)}
                              disabled={row.mode === 'new'}
                            >
                              <FaUndo />
                            </Button>
                          </span>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement='top'
                          overlay={<Tooltip id={`remove-${row.key}`}>Remove Row</Tooltip>}
                        >
                          <span>
                            <Button
                              variant='outline-danger'
                              size='sm'
                              onClick={() => handleRemoveRow(row.key)}
                            >
                              <FaTrash />
                            </Button>
                          </span>
                        </OverlayTrigger>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className='text-muted me-auto'>
          Changes are not saved yet. This dialog currently supports reviewing and editing data only.
        </div>
        <Button variant='secondary' onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkEditLayout;
